import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';

type ImageItem = { src: string; alt?: string } | string;

type Props = {
  images: ImageItem[];
  initialIndex?: number;
  gridClassName?: string;
  thumbClassName?: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onCompare: (subjectImg: string, comparableImg: string) => void;
};

const Gallery: React.FC<Props> = ({
  images,
  initialIndex = 0,
  gridClassName = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3',
  thumbClassName = 'h-36 w-full object-cover rounded-lg cursor-zoom-in',
  isOpen,
  setIsOpen,
  onCompare,
}) => {
  const normalized = useMemo(
    () =>
      images.map((it) => (typeof it === 'string' ? { src: it, alt: '' } : it)),
    [images],
  );

  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex]);

  const open = (i: number) => {
    setIndex(i);
    setIsOpen(true);
  };

  const close = () => setIsOpen(false);

  const next = () => setIndex((i) => (i + 1) % normalized.length);
  const prev = () =>
    setIndex((i) => (i - 1 + normalized.length) % normalized.length);

  // Lock scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  // Keyboard controls
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  return (
    isOpen &&
    createPortal(
      <LightboxModal
        image={normalized[index]}
        onClose={close}
        onNext={next}
        onPrev={prev}
        index={index}
        total={normalized.length}
        onCompare={onCompare}
      />,
      document.body,
    )
  );
};

export default Gallery;

/** Internal Modal Component */
const LightboxModal: React.FC<{
  image: { src: string; alt?: string };
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  index: number;
  total: number;
  onCompare: (subjectImg: string, comparableImg: string) => void;
}> = ({ image, onClose, onNext, onPrev, index, total, onCompare }) => {
  // Simple swipe handling
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 50) {
      if (dx < 0) onNext();
      else onPrev();
    }
    setTouchStartX(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      <div
        className="absolute inset-0 flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close button */}
        <div className="absolute top-4 right-4 flex items-center gap-4">
          {/* <Button onClick={() => setIsOpen(true)}>Select Image</Button> */}
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-xl"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Counter */}
        <div className="absolute top-4 left-4 text-white/80 text-sm select-none">
          {index + 1} / {total}
        </div>

        {/* Prev / Next */}
        <button
          onClick={onPrev}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl"
          aria-label="Previous"
        >
          ‹
        </button>
        <button
          onClick={onNext}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl"
          aria-label="Next"
        >
          ›
        </button>

        {/* Image */}
        <img
          src={image.src}
          alt={image.alt ?? ''}
          className="max-h-[90vh] max-w-[95vw] object-contain rounded-xl shadow-2xl"
          draggable={false}
        />
      </div>
      <CompareModal
        onClose={() => setIsOpen(false)}
        img={image.src}
        isOpen={isOpen}
        onCompare={onCompare}
      />
    </div>
  );
};

interface CompareModalProps {
  img: string;
  onCompare: (subjectImg: string, comparableImg: string) => void;
  onClose: () => void;
  isOpen: boolean;
}

const CompareModal: FC<CompareModalProps> = ({
  img,
  onCompare,
  onClose,
  isOpen,
}) => {
  const propertyInfo = JSON.parse(
    window.localStorage.getItem('propertyInfo') || null,
  );

  const [selectedPhoto, setSelectedPhoto] = useState('');
  const [isOpenGallery, setIsOpenGallery] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="min-w-fit">
        <p>Compare</p>
        <Button
          onClick={() => onCompare(selectedPhoto, img)}
          disabled={!selectedPhoto}
        >
          Compare
        </Button>
        <div className="flex divide-x-2 gap-4 items-center">
          <img src={img} className="h-[280px] aspect-auto min-w-[380px]" />
          <div className="pl-4 min-w-[380px] flex h-full justify-center items-center">
            {selectedPhoto ? (
              <img
                src={selectedPhoto}
                className="h-[280px] aspect-auto cursor-pointer"
                onClick={() => setIsOpenGallery(true)}
              />
            ) : (
              <Button onClick={() => setIsOpenGallery(true)}>
                Select Photo
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
      <Dialog open={isOpenGallery} onOpenChange={() => setIsOpenGallery(false)}>
        <DialogContent className="min-w-fit">
          <p className="font-semibold">Property Images</p>
          <div className="flex gap-4 flex-wrap h-full justify-center items-center w-[710px]">
            {propertyInfo?.mls?.Media?.map((m) => (
              <img
                src={m.MediaURL}
                className="h-[150px] aspect-auto cursor-pointer"
                onClick={() => {
                  setSelectedPhoto(m.MediaURL);
                  setIsOpenGallery(false);
                }}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
